/**
 * Spatial Typography — words as field elements.
 *
 * Not UI. Not overlays. Not tooltips.
 * These are spatial objects inhabiting the same volume as the particles.
 * Typography IS data. The names ARE the field.
 *
 * Inspired by: McCandless data visualization, immersive spatial type.
 * Not: dashboard labels, floating annotations, chart axes.
 */

import * as THREE from 'three';
import type { SyntheticField, SyntheticNode } from '../data/synthetic';
import { useAuraStore } from '../state/store';

interface CategoryInfo {
  name: string;
  center: THREE.Vector3;
  color: THREE.Color;
  nodeCount: number;
  clusters: { name: string; position: THREE.Vector3; intensity: number }[];
}

export class FieldLabels {
  public group: THREE.Group;
  private categorySprites: THREE.Sprite[] = [];
  private clusterSprites: THREE.Sprite[] = [];
  private haloSprites: THREE.Sprite[] = [];
  private categories: CategoryInfo[] = [];

  constructor(field: SyntheticField) {
    this.group = new THREE.Group();
    this.categories = this.extractCategories(field);
    this.createCategoryHalos();
    this.createCategoryLabels();
    this.createClusterLabels();
  }

  /**
   * Group nodes by category, compute centers and average colors.
   */
  private extractCategories(field: SyntheticField): CategoryInfo[] {
    const catMap = new Map<
      string,
      { nodes: SyntheticNode[]; mainClusters: SyntheticNode[] }
    >();

    for (const node of field.nodes) {
      if (!catMap.has(node.category)) {
        catMap.set(node.category, { nodes: [], mainClusters: [] });
      }
      const entry = catMap.get(node.category)!;
      entry.nodes.push(node);
      // Only real cluster center nodes — not entity satellites (cluster-xxx-e0, cluster-xxx-e1)
      if (node.id.startsWith('cluster-') && !node.id.match(/-e\d+$/)) {
        entry.mainClusters.push(node);
      }
    }

    return Array.from(catMap.entries()).map(([name, { nodes, mainClusters }]) => {
      // Centroid from main clusters
      const center = new THREE.Vector3();
      for (const node of mainClusters) {
        center.add(new THREE.Vector3(...node.position));
      }
      center.divideScalar(mainClusters.length || 1);

      // Average color, brightened for readability
      const avgColor = new THREE.Color(0, 0, 0);
      for (const node of mainClusters) {
        avgColor.r += node.color[0];
        avgColor.g += node.color[1];
        avgColor.b += node.color[2];
      }
      const n = mainClusters.length || 1;
      avgColor.r = Math.min(1, (avgColor.r / n) * 1.5 + 0.2);
      avgColor.g = Math.min(1, (avgColor.g / n) * 1.5 + 0.2);
      avgColor.b = Math.min(1, (avgColor.b / n) * 1.5 + 0.2);

      return {
        name,
        center,
        color: avgColor,
        nodeCount: nodes.length,
        clusters: mainClusters.map((c) => ({
          name: c.name,
          position: new THREE.Vector3(...c.position),
          intensity: c.intensity,
        })),
      };
    });
  }

  // ─── Canvas text rendering ───────────────────────────────────

  /**
   * Render text to canvas with optional glow and letter-spacing.
   */
  private renderText(
    text: string,
    fontSize: number,
    color: string,
    options: {
      bold?: boolean;
      glow?: boolean;
      glowColor?: string;
      letterSpacing?: number;
    } = {},
  ): HTMLCanvasElement {
    const { bold = false, glow = false, glowColor, letterSpacing = 0 } = options;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d')!;

    const dpr = 2;
    const weight = bold ? '700' : '300';
    const font = `${weight} ${fontSize * dpr}px Inter, -apple-system, BlinkMacSystemFont, Helvetica, sans-serif`;

    // Measure first
    ctx.font = font;
    let textWidth: number;
    if (letterSpacing > 0) {
      textWidth = 0;
      for (const char of text) {
        textWidth += ctx.measureText(char).width + letterSpacing * dpr;
      }
    } else {
      textWidth = ctx.measureText(text).width;
    }

    const pad = glow ? fontSize * dpr * 1.2 : fontSize * dpr * 0.5;
    canvas.width = Math.ceil(textWidth + pad * 2);
    canvas.height = Math.ceil(fontSize * dpr * 2.8 + pad * 2);

    // Must re-set font after canvas resize
    ctx.font = font;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    const cx = canvas.width / 2;
    const cy = canvas.height / 2;

    // Glow pass
    if (glow) {
      const gc = glowColor || color;
      ctx.save();

      // Outer glow
      ctx.shadowColor = gc;
      ctx.shadowBlur = fontSize * dpr * 1.5;
      ctx.fillStyle = gc;
      ctx.globalAlpha = 0.15;
      this.drawText(ctx, text, cx, cy, letterSpacing * dpr);

      // Inner glow
      ctx.shadowBlur = fontSize * dpr * 0.6;
      ctx.globalAlpha = 0.35;
      this.drawText(ctx, text, cx, cy, letterSpacing * dpr);

      ctx.restore();
    }

    // Main text
    ctx.fillStyle = color;
    ctx.globalAlpha = 1.0;
    this.drawText(ctx, text, cx, cy, letterSpacing * dpr);

    return canvas;
  }

  /**
   * Draw text with optional letter-spacing.
   */
  private drawText(
    ctx: CanvasRenderingContext2D,
    text: string,
    cx: number,
    cy: number,
    spacing: number,
  ) {
    if (spacing > 0) {
      let totalWidth = 0;
      for (const char of text) {
        totalWidth += ctx.measureText(char).width + spacing;
      }
      totalWidth -= spacing;

      let x = cx - totalWidth / 2;
      for (const char of text) {
        const charWidth = ctx.measureText(char).width;
        ctx.fillText(char, x + charWidth / 2, cy);
        x += charWidth + spacing;
      }
    } else {
      ctx.fillText(text, cx, cy);
    }
  }

  /**
   * Create a THREE.Sprite from a canvas.
   */
  private spriteFromCanvas(
    canvas: HTMLCanvasElement,
    options: { depthTest?: boolean; opacity?: number } = {},
  ): THREE.Sprite {
    const { depthTest = false, opacity = 1 } = options;
    const texture = new THREE.CanvasTexture(canvas);
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;

    const material = new THREE.SpriteMaterial({
      map: texture,
      transparent: true,
      blending: THREE.NormalBlending,
      depthWrite: false,
      depthTest,
      opacity,
    });

    return new THREE.Sprite(material);
  }

  // ─── Category halos (field territory indicators) ─────────────

  private createCategoryHalos() {
    for (const cat of this.categories) {
      const canvas = this.createHaloCanvas(cat.color);
      const sprite = this.spriteFromCanvas(canvas, { opacity: 0 });

      sprite.position.copy(cat.center);

      // Very large — defines the "zone"
      const scale = 350 + cat.nodeCount * 2;
      sprite.scale.set(scale, scale, 1);

      this.haloSprites.push(sprite);
      this.group.add(sprite);
    }
  }

  /**
   * Radial gradient canvas for territory halos.
   */
  private createHaloCanvas(color: THREE.Color): HTMLCanvasElement {
    const size = 256;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d')!;

    const r = Math.round(color.r * 255);
    const g = Math.round(color.g * 255);
    const b = Math.round(color.b * 255);

    const gradient = ctx.createRadialGradient(
      size / 2, size / 2, 0,
      size / 2, size / 2, size / 2,
    );
    gradient.addColorStop(0, `rgba(${r}, ${g}, ${b}, 0.12)`);
    gradient.addColorStop(0.3, `rgba(${r}, ${g}, ${b}, 0.06)`);
    gradient.addColorStop(0.7, `rgba(${r}, ${g}, ${b}, 0.02)`);
    gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');

    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, size, size);

    return canvas;
  }

  // ─── Category labels (large, bold, spatial) ──────────────────

  private createCategoryLabels() {
    for (const cat of this.categories) {
      const { r, g, b } = cat.color;
      const colorStr = `rgb(${Math.round(r * 255)}, ${Math.round(g * 255)}, ${Math.round(b * 255)})`;

      const canvas = this.renderText(cat.name.toUpperCase(), 18, colorStr, {
        bold: true,
        glow: true,
        letterSpacing: 3,
      });

      const sprite = this.spriteFromCanvas(canvas, { opacity: 0 });

      // Float above category center
      const pos = cat.center.clone();
      pos.y += 60;
      sprite.position.copy(pos);

      // Scale preserving aspect ratio — category labels subtler
      const aspect = canvas.width / canvas.height;
      const baseScale = 32;
      sprite.scale.set(baseScale * aspect, baseScale, 1);

      this.categorySprites.push(sprite);
      this.group.add(sprite);
    }
  }

  // ─── Cluster labels (smaller, proximity-revealed) ────────────

  private createClusterLabels() {
    for (const cat of this.categories) {
      const { r, g, b } = cat.color;
      const colorStr = `rgb(${Math.round(r * 220)}, ${Math.round(g * 220)}, ${Math.round(b * 220)})`;

      for (const cluster of cat.clusters) {
        const canvas = this.renderText(cluster.name, 11, colorStr, {
          bold: false,
          glow: false,
        });

        const sprite = this.spriteFromCanvas(canvas, { opacity: 0 });

        // Position slightly above cluster
        const pos = cluster.position.clone();
        pos.y += 18;
        sprite.position.copy(pos);

        const aspect = canvas.width / canvas.height;
        // Smaller, subtler labels — the field is the signal, not the text
        const baseScale = 10 + cluster.intensity * 5;
        sprite.scale.set(baseScale * aspect, baseScale, 1);

        sprite.userData = { intensity: cluster.intensity, category: cat.name };

        this.clusterSprites.push(sprite);
        this.group.add(sprite);
      }
    }
  }

  /**
   * Get category data (used by zone navigation).
   */
  getCategories(): CategoryInfo[] {
    return this.categories;
  }

  /**
   * Get category sprites (used by zone navigation for raycasting).
   */
  getCategorySprites(): THREE.Sprite[] {
    return this.categorySprites;
  }

  // ─── Per-frame update ────────────────────────────────────────

  private activeZone: string | null = null;

  /** Called by zone navigation when entering/exiting a zone. */
  setActiveZone(categoryName: string | null) {
    this.activeZone = categoryName;
  }

  /**
   * Labels appear after particles during entry ritual.
   * Cluster labels are proximity-revealed (closer = brighter).
   * When a zone is active, that zone's clusters are force-revealed.
   */
  update(entryProgress: number, cameraPosition: THREE.Vector3) {
    // Labels enter during the second half of the ritual
    const rawFade = Math.max(0, (entryProgress - 0.55) / 0.45);
    const fade = rawFade * rawFade * (3 - 2 * rawFade); // smoothstep

    // Validate mode: force-reveal ALL labels — "confirms structure already visible"
    const { mode, modeTransitionProgress } = useAuraStore.getState();
    const validateReveal = mode === 'validate' ? modeTransitionProgress : 0;

    // Halos — very subtle zone color wash (dim in validate — diagram doesn't need hazes)
    for (const sprite of this.haloSprites) {
      const mat = sprite.material as THREE.SpriteMaterial;
      const baseOpacity = fade * 0.2;
      mat.opacity = baseOpacity * (1 - validateReveal * 0.7);
    }

    // Category labels — HIDDEN BY DEFAULT per design brief.
    // "Names must appear late, appear softly, be summoned or earned."
    // EXCEPTION: Validate mode force-reveals all.
    for (const sprite of this.categorySprites) {
      const mat = sprite.material as THREE.SpriteMaterial;
      const dist = sprite.position.distanceTo(cameraPosition);
      // Only begin appearing within ~350 units (well inside the zone)
      const proximityFade = 1 - Math.min(Math.max((dist - 150) / 200, 0), 1);
      const baseOpacity = fade * proximityFade * 0.45;
      // Validate: force reveal — all category names become visible
      const validateOpacity = fade * 0.75;
      mat.opacity = Math.max(baseOpacity, validateOpacity * validateReveal);
    }

    // Cluster labels — proximity-revealed OR force-revealed by active zone OR validate mode
    for (const sprite of this.clusterSprites) {
      const dist = sprite.position.distanceTo(cameraPosition);
      const intensity = sprite.userData?.intensity ?? 0.5;
      const isInActiveZone = this.activeZone && sprite.userData?.category === this.activeZone;

      let targetOpacity: number;
      if (isInActiveZone) {
        // Force reveal: visible regardless of distance, with gentle dist mod
        const distMod = 0.5 + 0.5 * (1 - Math.min(dist / 500, 1));
        targetOpacity = fade * distMod * (0.4 + intensity * 0.5);
      } else {
        // Default: very tight proximity only
        const distFade = 1 - Math.min(Math.max((dist - 60) / 160, 0), 1);
        targetOpacity = fade * distFade * (0.25 + intensity * 0.4);
      }

      // Validate: force-reveal ALL cluster labels — diagram must be legible
      const validateLabelOpacity = fade * (0.3 + intensity * 0.5);
      targetOpacity = Math.max(targetOpacity, validateLabelOpacity * validateReveal);

      const mat = sprite.material as THREE.SpriteMaterial;
      mat.opacity = targetOpacity;
    }
  }

  dispose() {
    const all = [...this.categorySprites, ...this.clusterSprites, ...this.haloSprites];
    for (const sprite of all) {
      const mat = sprite.material as THREE.SpriteMaterial;
      if (mat.map) mat.map.dispose();
      mat.dispose();
    }
  }
}
